import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react'

/**
 * Hook per gestire stato persistito su localStorage.
 *
 * Gestore automaticamente:
 * - Lettura init da localStorage con fallback a initialValue
 * - Validazione durante deserialization
 * - Sync con localStorage quando lo state cambia
 * - Error handling per storage errors (quota, permission, etc.)
 *
 * @param key - localStorage key
 * @param initialValue - Valore default se localStorage è vuoto o non valido
 * @param options - Opzioni per serializzazione/deserializzazione
 * @returns [state, setState] come useState
 *
 * @example
 * const [user, setUser] = useLocalStorage('current-user', null, {
 *   serialize: JSON.stringify,
 *   deserialize: (s) => JSON.parse(s),
 *   validate: (v) => typeof v === 'object' && v !== null && 'id' in v
 * })
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  options?: {
    serialize?: (value: T) => string
    deserialize?: (stored: string) => unknown
    validate?: (value: unknown) => boolean
  },
): [T, Dispatch<SetStateAction<T>>] {
  const { serialize = JSON.stringify, deserialize = JSON.parse, validate } = options ?? {}

  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key)
      if (stored === null) return initialValue
      const deserialized = deserialize(stored)
      // If validate is provided, check before returning
      if (validate && !validate(deserialized)) return initialValue
      return deserialized as T
    } catch {
      return initialValue
    }
  })

  const setStateAndPersist = useCallback(
    (value: SetStateAction<T>) => {
      setState((prev) => {
        const next = typeof value === 'function' ? (value as (prev: T) => T)(prev) : value
        try {
          localStorage.setItem(key, serialize(next))
        } catch {
          // ignore storage errors (quota exceeded, permission denied, etc.)
        }
        return next
      })
    },
    [key, serialize],
  )

  // Optional: sync state changes across tabs
  useEffect(() => {
    function handleStorageChange(e: StorageEvent) {
      if (e.key === key && e.newValue !== null) {
        try {
          const deserialized = deserialize(e.newValue)
          if (!validate || validate(deserialized)) {
            setState(deserialized as T)
          }
        } catch {
          // ignore deserialization errors
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [key, deserialize, validate])

  return [state, setStateAndPersist]
}
